import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import List from "@mui/material/List";
import Typography from "@mui/material/Typography";
import type {
  ConversationResponse as Conversation,
  TagCount,
} from "../../../api/generated";
import { ConversationItem } from "./ConversationItem";

interface ConversationListProps {
  conversations: Conversation[] | undefined;
  isLoading: boolean;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  onRename: (id: string, title: string) => void;
  onAddTag: (id: string, tag: string) => void;
  onRemoveTag: (id: string, tag: string) => void;
  onTagClick: (tag: string) => void;
  tagSuggestions: TagCount[];
  hasFilters?: boolean;
  bulkMode?: boolean;
  selectedIds?: Set<string>;
  onBulkToggle?: (id: string) => void;
}

export function ConversationList({
  conversations,
  isLoading,
  selectedId,
  onSelect,
  onDelete,
  onRename,
  onAddTag,
  onRemoveTag,
  onTagClick,
  tagSuggestions,
  hasFilters,
  bulkMode,
  selectedIds,
  onBulkToggle,
}: ConversationListProps) {
  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!conversations?.length) {
    return (
      <Box sx={{ px: 2, py: 4, textAlign: "center" }}>
        <Typography variant="body2" sx={{
          color: "text.secondary"
        }}>
          {hasFilters
            ? "No conversations match your search."
            : "No conversations yet. Start a new one!"}
        </Typography>
      </Box>
    );
  }

  return (
    <List sx={{ px: 1 }}>
      {conversations.map((conv) => (
        <ConversationItem
          key={conv.id}
          conversation={conv}
          selected={conv.id === selectedId}
          onClick={() => onSelect(conv.id)}
          onDelete={() => onDelete(conv.id)}
          onRename={(title) => onRename(conv.id, title)}
          onAddTag={(tag) => onAddTag(conv.id, tag)}
          onRemoveTag={(tag) => onRemoveTag(conv.id, tag)}
          onTagClick={onTagClick}
          tagSuggestions={tagSuggestions}
          bulkMode={bulkMode}
          bulkSelected={selectedIds?.has(conv.id)}
          onBulkToggle={() => onBulkToggle?.(conv.id)}
        />
      ))}
    </List>
  );
}
